import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import * as amplitude from '@amplitude/analytics-browser';
import { jwtDecode } from 'jwt-decode';

import axios from '../axios';
// Use new hooks instead of legacy context
import { useAppState } from '../contexts/AppStateProvider';
import { useJWT } from '../contexts/JWTProvider';
import { oauthService } from '../services/OAuthService';
import { ServiceThemeOptions } from '../themes';
import { LinkToken } from '../types/LinkToken';
import { Analytics, createAnalyticsContext } from '../utils/analytics';
import { getNavigationAction } from '../utils/jwtHelpers';
import { parseLinkState } from '../utils/linkState';
import { safeRedirectReplace } from '../utils/urlUtils';
import { LinkPage, handleLeaveLink } from './ButtonExitLink';

export default function OAuthRedirect({ TSP }: { TSP: string }) {
  // Use new centralized hooks
  const {
    onboarded,
    redirectURI,
    setOnboarded,
    setRedirectURI,
    redirectURISuccess,
    serviceName,
    setTspCount,
    tspCount,
  } = useAppState();

  const { search } = useLocation();
  const navigate = useNavigate();
  const hasCalledAPI = useRef(false);
  const theme = useTheme() as ServiceThemeOptions;
  const jwt = useJWT();

  // Parse OAuth callback using centralized service
  const oauthCallback = oauthService.parseOAuthCallback(
    new URLSearchParams(search)
  );

  // Extract parameters for backward compatibility
  const APIKey = oauthCallback.success ? oauthCallback.code : null;
  const linkState = oauthCallback.state;
  const parsedLinkState = parseLinkState(linkState);
  const defaultLinkState = ['null', 'false', 'false', 'null', 'null'] as const;

  const token = parsedLinkState?.token ?? defaultLinkState[0];
  const updatingTSP = parsedLinkState?.updatingTSP ?? defaultLinkState[1];
  const magicLink = parsedLinkState?.magicLink ?? defaultLinkState[2];
  const serviceID = parsedLinkState?.serviceID ?? defaultLinkState[3];
  const fleetCode = parsedLinkState?.fleetCode ?? defaultLinkState[4];

  if (linkState && !parsedLinkState) {
    console.warn('[OAuthRedirect] Unable to parse stored linkState payload', {
      tsp: TSP,
      linkState,
    });
  }

  // OAuth error details (if any)
  const oauthError = oauthCallback.error;

  // Debug logging for OAuth parameters
  console.log('[OAuthRedirect] OAuth parameters extracted:', {
    tsp: TSP,
    hasLinkState: !!linkState,
    linkStateLength: linkState?.length,
    token: token === 'null' ? 'NULL_STRING' : token ? 'PRESENT' : 'MISSING',
    updatingTSP,
    magicLink,
    serviceID: serviceID === 'null' ? 'NULL_STRING' : serviceID,
    fleetCode: fleetCode === 'null' ? 'NULL_STRING' : fleetCode,
    linkStateSource: parsedLinkState?.source || 'unknown',
    jwtFromContext: {
      hasRawToken: !!jwt.rawToken,
      isValid: jwt.isValid,
      serviceId: jwt.serviceId,
      fleetCode: jwt.fleetCode,
    },
  });

  // Debug OAuth callback parsing
  console.log('[OAuthRedirect] OAuth callback details:', {
    tsp: TSP,
    rawSearch: search,
    oauthCallbackSuccess: oauthCallback.success,
    oauthCallbackCode: oauthCallback.code,
    oauthCallbackState: oauthCallback.state,
    oauthCallbackError: oauthCallback.error,
    extractedAPIKey: APIKey,
    extractedLinkState: linkState,
  });

  let decodedToken: LinkToken | null = null;
  if (token && token !== 'null') {
    try {
      decodedToken = jwtDecode<LinkToken>(token);
    } catch (error) {
      console.error('[OAuthRedirect] Failed to decode linkState token:', error);
    }
  }

  amplitude.setUserId('service-' + String(decodedToken?.sid));
  amplitude.setDeviceId('fleet-code-' + decodedToken?.fc);

  // Extract theme colors safely
  const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';
  const textPrimaryColor = (theme.palette?.text as any)?.primary || '#000000';
  const textSecondaryColor =
    (theme.palette?.text as any)?.secondary || '#757575';
  const fontFamily =
    (theme.typography as any)?.fontFamily || 'Inter, sans-serif';
  const paperBgColor = theme.palette?.background?.paper || '#FFFFFF';

  // Enhanced loading state with ErrorPage format
  const LoadingStateDiv = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Icon/Image Container */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          pt: 3,
          pb: 2,
        }}
      >
        <img
          src='/images/truck.png'
          alt='truck'
          loading='lazy'
          width={94}
          height={94}
          style={{ objectFit: 'contain' }}
        />
      </Box>

      {/* Loading spinner */}
      <Box sx={{ mb: 2 }}>
        <CircularProgress
          size={40}
          thickness={3}
          sx={{
            color: primaryColor,
          }}
        />
      </Box>

      {/* Main linking message */}
      <Typography
        variant='h4'
        sx={{
          mb: 1,
          fontFamily: fontFamily,
          fontWeight: 600,
          fontSize: '28px',
          lineHeight: '36px',
          textAlign: 'center',
          color: textPrimaryColor,
        }}
      >
        Linking {TSP}...
      </Typography>

      {/* Subtitle */}
      <Typography
        variant='body1'
        sx={{
          mb: 3,
          fontFamily: fontFamily,
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '24px',
          letterSpacing: '0.15px',
          textAlign: 'center',
          color: textPrimaryColor,
        }}
      >
        We're securely connecting your {TSP} account. This should only take a
        moment.
      </Typography>
    </Box>
  );

  function CheckDiv() {
    amplitude.track('Successfully Linked', {
      tsp: TSP,
      serviceName: serviceName,
    });
    amplitude.flush();
    return <>{redirectURI ? <RedirectingDiv /> : onboardedDiv}</>;
  }

  function RedirectingDiv() {
    safeRedirectReplace(redirectURI);
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          py: 4,
        }}
      >
        {/* Loading spinner for redirect */}
        <CircularProgress
          size={48}
          thickness={3}
          sx={{
            color: primaryColor,
          }}
        />

        <Typography
          variant='h5'
          sx={{
            fontFamily: fontFamily,
            fontWeight: 600,
            fontSize: '24px',
            lineHeight: '30px',
            textAlign: 'center',
            color: textPrimaryColor,
            mb: 1,
          }}
        >
          {TSP} account linked!
        </Typography>

        <Typography
          variant='body1'
          sx={{
            fontFamily: fontFamily,
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '24px',
            textAlign: 'center',
            color: textSecondaryColor,
            letterSpacing: '0.15px',
          }}
        >
          Redirecting...
        </Typography>
      </Box>
    );
  }

  useEffect(() => {
    if (hasCalledAPI.current) {
      return;
    }

    hasCalledAPI.current = true;

    // Handle null linkState gracefully
    if (!linkState || !decodedToken) {
      console.error('[OAuthRedirect] Missing linkState parameter in URL:', {
        search,
        pathname: window.location.pathname,
      });
      return;
    }

    // Handle OAuth error parameters - these are sent by OAuth providers when authorization fails
    if (oauthError) {
      console.error('[OAuthRedirect] OAuth authorization error:', {
        error: oauthError,
        tsp: TSP,
        search,
      });

      // Track OAuth error through analytics system (handles Slack automatically)
      const analyticsContext = createAnalyticsContext(jwt, serviceName, {
        tsp: TSP,
      });
      Analytics.connectionError(
        analyticsContext,
        'oauth_authorization_error',
        `${oauthError.code}: ${oauthError.description || 'OAuth authorization failed'}`,
        'oauth_redirect'
      ).catch((error) => {
        console.error(
          '[OAuthRedirect] Error tracking OAuth authorization error:',
          error
        );
      });

      // Track OAuth authorization error for analytics
      amplitude.track('OAuth Authorization Error', {
        tsp: TSP,
        serviceName: serviceName,
        oauthError: oauthError.code,
        oauthErrorDescription: oauthError.description,
        hasLinkState: !!linkState,
      });

      // Get user-friendly error message using centralized service
      const errorDetails = oauthService.getErrorMessage(oauthError, TSP);

      // Use new navigation action system
      const navigationAction = getNavigationAction(jwt);

      // Navigate to error page with OAuth-specific error details
      navigate('/error', {
        state: {
          errorMsg: errorDetails.title,
          errorCaption: errorDetails.message,
          errorType: 'oauth_authorization_error',
          errorDetails: {
            oauthError: oauthError.code,
            oauthErrorDescription: oauthError.description,
            oauthErrorUri: oauthError.uri,
          },
          navigationAction: navigationAction,
        },
      });
      return;
    }

    // Track OAuth redirect page load with initial parameters
    amplitude.track('OAuth Redirect Page Load', {
      tsp: TSP,
      serviceName: serviceName,
      hasAPIKey: !!APIKey,
      hasLinkState: !!linkState,
      isUpdatingTSP: updatingTSP === 'true',
      isMagicLink: magicLink === 'true',
    });

    // Success case: we have an authorization code and can proceed with onboarding
    if (APIKey) {
      console.log(
        `[OAuthRedirect] OAuth authorization successful for ${TSP}:`,
        {
          hasAPIKey: !!APIKey,
          hasLinkState: !!linkState,
          tsp: TSP,
        }
      );
    } else {
      console.warn(
        `[OAuthRedirect] No authorization code received for ${TSP}:`,
        {
          search,
          hasLinkState: !!linkState,
          tsp: TSP,
        }
      );
    }

    // Retrieve stored credentials from sessionStorage
    let storedUsername = '';
    let storedPassword = '';

    try {
      storedUsername =
        sessionStorage.getItem(`${TSP.toLowerCase()}_username`) || '';
      storedPassword =
        sessionStorage.getItem(`${TSP.toLowerCase()}_password`) || '';
    } catch (error) {
      // Continue with the flow even if sessionStorage fails
    }

    // Get TSP-specific OAuth configuration for payload customization
    const tspKey = TSP.toLowerCase();
    const tspOAuthConfig = oauthService.getProvider(tspKey);
    const useApiKeyAsAuthCode =
      tspOAuthConfig?.callbackOptions?.useApiKeyAsAuthorizationCode || false;
    const apiFieldMappings = tspOAuthConfig?.callbackOptions?.apiFieldMappings;

    // Build payload to match backend struct field names exactly
    const payload: any = {
      apiKey: APIKey,
      tsp: tspKey,
      motiveRedirectURI:
        tspKey === 'motive' && tspOAuthConfig
          ? tspOAuthConfig.getRedirectUri(linkState!)
          : '',
      bouncieRedirectURI:
        tspKey === 'bouncie' && tspOAuthConfig
          ? tspOAuthConfig.getRedirectUri(linkState!)
          : '',
      authorizationCode: useApiKeyAsAuthCode && APIKey ? APIKey : '',
      updateTSPConfirmed: updatingTSP === 'true',
      username: storedUsername,
      password: storedPassword,
    };

    // Add any additional fields specified in TSP configuration
    if (apiFieldMappings?.additionalFields) {
      Object.entries(apiFieldMappings.additionalFields).forEach(
        ([fieldName, sourceField]) => {
          if (sourceField === 'apiKey' && APIKey) {
            payload[fieldName] = APIKey;
          }
          // Add more source field mappings here as needed
        }
      );
    }

    // Debug redirect URI generation
    if (tspKey === 'bouncie' && tspOAuthConfig) {
      console.log(`[OAuthRedirect] Generated Bouncie redirect URI:`, {
        redirectUri: payload.bouncieRedirectURI,
        linkState: linkState ? `${linkState.substring(0, 50)}...` : 'null',
        baseUrl: process.env.REACT_APP_REDIRECT_URL,
      });
    }

    // Debug logging for OAuth flow
    console.log(`[OAuthRedirect] Processing ${TSP} OAuth callback:`, {
      hasAPIKey: !!APIKey,
      APIKey: APIKey ? `${APIKey.substring(0, 10)}...` : 'null',
      hasLinkState: !!linkState,
      linkState: linkState ? `${linkState.substring(0, 50)}...` : 'null',
      tspConfig: tspOAuthConfig ? 'found' : 'not found',
      apiFieldMappings,
      useApiKeyAsAuthCode,
    });

    // Debug the actual payload being sent to backend
    console.log(`[OAuthRedirect] Payload being sent to backend:`, {
      tsp: TSP,
      payloadKeys: Object.keys(payload),
      payload: {
        ...payload,
        // Mask sensitive data in logs
        username: storedUsername ? '[PRESENT]' : '[EMPTY]',
        password: storedPassword ? '[PRESENT]' : '[EMPTY]',
        api_key: APIKey ? `${APIKey.substring(0, 10)}...` : 'null',
        authorization_code: payload.authorization_code
          ? `${payload.authorization_code.substring(0, 10)}...`
          : 'null',
      },
    });

    axios
      .post('/service/onboard', payload, {
        headers: {
          Authorization: (() => {
            // If token from linkState is valid (not 'null' string), use it
            if (token && token !== 'null') {
              return 'Bearer ' + token;
            }

            // Fallback to JWT context token if available
            if (jwt.rawToken && jwt.isValid) {
              console.log(
                '[OAuthRedirect] Using JWT context token as fallback'
              );
              return 'Bearer ' + jwt.rawToken;
            }

            // Log the issue and return empty (will likely cause 403, but at least we know why)
            console.error(
              '[OAuthRedirect] No valid token available for API call:',
              {
                linkStateToken: token,
                jwtToken: jwt.rawToken ? 'present' : 'missing',
                jwtValid: jwt.isValid,
                tsp: TSP,
              }
            );
            return '';
          })(),
        },
      })
      .then(async (res) => {
        // Clear stored credentials for security
        try {
          sessionStorage.removeItem(`${TSP.toLowerCase()}_username`);
          sessionStorage.removeItem(`${TSP.toLowerCase()}_password`);
          console.log(`[OAuthRedirect] Cleared stored credentials for ${TSP}`);
        } catch (error) {
          console.error(
            `[OAuthRedirect] Error clearing credentials from sessionStorage:`,
            error
          );
          // Continue with the flow even if sessionStorage fails
        }

        if (res.status === 201) {
          // Since API doesn't return tsp_count, we need to increment manually
          // For new connections (not updates), increment the current count by 1
          let finalTspCount = tspCount;
          if (updatingTSP !== 'true') {
            finalTspCount = tspCount + 1;
            console.log(
              '[OAuthRedirect] Success response - incrementing tspCount for new TSP connection from',
              tspCount,
              'to',
              finalTspCount,
              'API response:',
              res.data
            );
            setTspCount(finalTspCount);
          } else {
            console.log(
              '[OAuthRedirect] TSP update - not incrementing tspCount, API response:',
              res.data
            );
          }

          // Track successful API response with details
          amplitude.track('TSP API Response Success', {
            tsp: TSP,
            serviceName: serviceName,
            responseStatus: res.status,
            hasRedirectURI: !!res.data['RedirectURI'],
            hasSuccessURI: !!(redirectURISuccess || decodedToken?.urisuccess),
            isMagicLink: magicLink === 'true',
            tspCount: finalTspCount,
          });

          // Send webhooks and track in Slack through analytics system
          const analyticsContext = createAnalyticsContext(jwt, serviceName, {
            tsp: TSP,
          });

          // Send TSP_ONBOARDING_STARTED webhook and track credentials entered (Analytics handles Slack)
          await Analytics.credentialsEntered(
            analyticsContext,
            TSP,
            'oauth_credentials',
            updatingTSP === 'true'
          );

          // Send TSP_ONBOARDING_COMPLETED webhook and track completion (Analytics handles Slack)
          await Analytics.connectionSuccessful(analyticsContext, TSP);

          // Always navigate to success page instead of auto-redirecting
          // This allows users to choose "Add Another Provider" or return to service
          navigate('/success', {
            state: {
              successMsg: `${TSP} account linked successfully!`,
              successCaption:
                'Your telematics data is now connected and ready to use.',
            },
          });

          // Track final success state
          amplitude.track('Successfully Linked', {
            tsp: TSP,
            serviceName: serviceName,
            redirectType: redirectURISuccess
              ? 'success_uri'
              : decodedToken?.urisuccess
                ? 'token_success_uri'
                : magicLink === 'true'
                  ? 'magic_link'
                  : 'default',
            fleetCode: decodedToken?.fc || null,
            serviceId: decodedToken?.sid || null,
          });
        }
      })
      .catch((err) => {
        console.log('[OAuthRedirect] API call failed, entering catch block:', {
          errorStatus: err.response?.status,
          errorMessage: err.response?.data?.message || err.message,
          errorData: err.response?.data,
        });

        // Debug error tracking context
        console.log('[OAuthRedirect] Error tracking context:', {
          jwt: {
            hasRawToken: !!jwt.rawToken,
            isValid: jwt.isValid,
            serviceId: jwt.serviceId,
            fleetCode: jwt.fleetCode,
          },
          serviceName,
          tsp: TSP,
        });

        // Track API error through analytics system (handles Slack automatically)
        const analyticsContext = createAnalyticsContext(jwt, serviceName, {
          tsp: TSP,
        });
        Analytics.connectionError(
          analyticsContext,
          'oauth_api_error',
          err.response?.data?.message || err.message || 'Unknown API error',
          'OAuthRedirect'
        ).catch((error) => {
          console.error(
            '[OAuthRedirect] Error tracking connection error:',
            error
          );
        });

        // Track detailed error information
        amplitude.track('Error Linking', {
          tsp: TSP,
          serviceName: serviceName,
          errorStatus: err.response?.status,
          errorMessage: err.response?.data?.message || err.message,
          errorCode: err.response?.data?.code,
          hasAPIKey: !!APIKey,
          hasLinkState: !!linkState,
          isUpdatingTSP: updatingTSP === 'true',
        });
        console.log('Error! Onboarding rejected:', err);

        // Use new navigation action system instead of legacy showTryAgain/showSkip
        const navigationAction = getNavigationAction(jwt);

        // Navigate to proper error page with new navigation system
        navigate('/error', {
          state: {
            errorMsg: `Error connecting to ${TSP}`,
            errorCaption:
              'There was a problem connecting to your telematics provider. Please try again or contact support for assistance.',
            errorType: 'oauth_error',
            navigationAction: navigationAction, // Use new system
          },
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    APIKey,
    fleetCode,
    linkState,
    magicLink,
    serviceID,
    setOnboarded,
    setRedirectURI,
    token,
    decodedToken,
    TSP,
    updatingTSP,
    serviceName,
    redirectURISuccess,
    // Include specific JWT properties used for analytics
    jwt.serviceId,
    jwt.fleetCode,
    jwt.rawToken,
    navigate,
  ]); // Performance optimized: specific JWT properties included, full object excluded to prevent unnecessary re-renders

  const onboardedDiv = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        py: 4,
      }}
    >
      <Typography
        variant='h5'
        sx={{
          fontFamily: fontFamily,
          fontWeight: 600,
          fontSize: '24px',
          lineHeight: '30px',
          textAlign: 'center',
          color: textPrimaryColor,
          mb: 1,
        }}
      >
        {TSP} account linked!
      </Typography>

      <Typography
        variant='body1'
        sx={{
          fontFamily: fontFamily,
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '24px',
          textAlign: 'center',
          color: textSecondaryColor,
          letterSpacing: '0.15px',
        }}
      >
        You can now close this page.
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '520px',
          maxWidth: 'calc(100% - 32px)',
          borderRadius: '8px',
          position: 'relative',
          zIndex: 1,
          p: '32px 50px 40px',
          bgcolor: paperBgColor,
        }}
      >
        {/* Handle error case */}
        {!linkState || !decodedToken ? (
          <>
            <Typography
              variant='h4'
              sx={{
                mb: 1,
                fontFamily: fontFamily,
                fontWeight: 600,
                fontSize: '28px',
                lineHeight: '36px',
                textAlign: 'center',
                color: textPrimaryColor,
              }}
            >
              Invalid OAuth redirect
            </Typography>
            <Typography
              variant='body1'
              sx={{
                fontFamily: fontFamily,
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: '0.15px',
                textAlign: 'center',
                color: textPrimaryColor,
              }}
            >
              Missing required state parameter
            </Typography>
          </>
        ) : (
          <>
            {/* Main content area */}
            {onboarded ? <CheckDiv /> : <LoadingStateDiv />}

            {/* Optional go back button */}
            {decodedToken?.urierror && (
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Button
                  onClick={() =>
                    handleLeaveLink({
                      token: token,
                      redirectURIError: decodedToken?.urierror || 'null',
                      serviceName: serviceName,
                      pageOnExit: LinkPage.Motive,
                      selectedTSP: TSP,
                    })
                  }
                  sx={{
                    color: (theme.palette?.info as any)?.main || primaryColor,
                    textTransform: 'none',
                    fontFamily: fontFamily,
                  }}
                  startIcon={<ArrowBackIcon />}
                >
                  {decodedToken?.sid === 320 ? 'Go back to AtoB' : 'Go back'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}
